'use client'

import { useState } from 'react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { Alert, Box, Button, Stack, Text, TextInput } from '@mantine/core'
import { LuTriangleAlert } from 'react-icons/lu'
import { signIn } from '@lib/auth/client'

import { checkUserExists } from '../_actions/check-user-exists'
import classes from '../login.module.css'
import LoginSuccess from './LoginSuccess'

type LoginFormProps = {
    title: string
    logoAlt: string
}

export default function LoginForm({ title, logoAlt }: LoginFormProps) {
    const t = useTranslations('auth.login')
    const searchParams = useSearchParams()
    const prefillEmail = searchParams.get('email') ?? ''
    const invited = searchParams.get('invited') === '1'
    // better-auth appends `?error=<code>` to the errorCallbackURL when link verification fails.
    const linkErrorCode = searchParams.get('error')

    const [email, setEmail] = useState(prefillEmail)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [sent, setSent] = useState(false)

    let linkErrorMessage: string | null = null
    if (linkErrorCode === 'INVALID_TOKEN') linkErrorMessage = t('errors.linkInvalid')
    else if (linkErrorCode === 'new_user_signup_disabled') linkErrorMessage = t('errors.accountNotFound')
    else if (linkErrorCode) linkErrorMessage = t('errors.signInFailed')

    /** Sends a magic link. Resolves to an error message, or `null` when the mail went out. */
    const sendMagicLink = async (target: string): Promise<string | null> => {
        const exists = await checkUserExists(target)
        if (!exists) return t('errors.accountNotFound')

        const { error: signInError } = await signIn.magicLink({
            email: target,
            callbackURL: '/dashboard',
            errorCallbackURL: '/auth/login'
        })

        return signInError ? signInError.message || t('errors.signInFailed') : null
    }

    const handleLogin = async (e: React.BaseSyntheticEvent) => {
        e.preventDefault()

        if (!email) {
            setError('Email cannot be empty')
            return
        }
        setLoading(true)
        setError(null)

        const message = await sendMagicLink(email)

        setLoading(false)

        if (message) setError(message)
        else setSent(true)
    }

    if (sent) {
        return <LoginSuccess email={email} onResend={() => sendMagicLink(email)} onChangeEmail={() => setSent(false)} />
    }

    return (
        <Stack gap='xl' align='stretch' w='100%' miw={0}>
            <Stack gap='md' className={classes.brandBlock}>
                <Box className={classes.logoWrap} mx='auto'>
                    <Image
                        src='/logo/logo-dark.svg'
                        alt={logoAlt}
                        width={88}
                        height={88}
                        priority
                        className={classes.logoImg}
                    />
                </Box>
                <Text component='h1' className={classes.headline}>
                    {title}
                </Text>
            </Stack>

            <Box className={classes.formBlock}>
                <form onSubmit={handleLogin} noValidate>
                    <Stack gap='lg'>
                        {linkErrorMessage && (
                            <Alert
                                color='red'
                                variant='light'
                                radius='md'
                                icon={<LuTriangleAlert />}
                                title={t('errors.linkErrorTitle')}
                                role='alert'>
                                {linkErrorMessage}
                            </Alert>
                        )}
                        {invited && (
                            <Text component='p' className={classes.inviteHint}>
                                {t('invitationAccepted')}
                            </Text>
                        )}
                        <TextInput
                            type='email'
                            aria-label={t('email.label')}
                            placeholder={t('email.placeholder')}
                            value={email}
                            onChange={(e) => setEmail(e.currentTarget.value)}
                            required
                            size='lg'
                            radius='xl'
                            autoComplete='email'
                            inputMode='email'
                            error={error}
                        />
                        <Button type='submit' loading={loading} fullWidth size='xl' mt={4}>
                            {t('submit')}
                        </Button>
                    </Stack>
                </form>
            </Box>
        </Stack>
    )
}
